import { Mail, MessageSquare, Phone, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface MobileContactActionBarProps {
  phone: string;
  email?: string;
  variant?: 'inline' | 'fixed';
  onEmailClick?: () => void;
}

/**
 * 4-button contact bar (Email · SMS · Call · WhatsApp).
 * - `inline`: rendered below the header, full-width row.
 * - `fixed`: pinned to the bottom of the viewport on mobile/tablet.
 */
export function MobileContactActionBar({
  phone,
  email,
  variant = 'inline',
  onEmailClick,
}: MobileContactActionBarProps) {
  const cleanPhone = (phone || '').replace(/[^0-9+]/g, '');
  const waPhone = (phone || '').replace(/[^0-9]/g, '');

  const handleEmail = () => {
    if (onEmailClick) {
      onEmailClick();
    } else if (email) {
      window.location.href = `mailto:${email}`;
    } else {
      // Scroll to inline lead form section
      document.getElementById('lead-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const wrapperClass =
    variant === 'fixed'
      ? 'lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background border-t shadow-lg px-3 py-2'
      : 'w-full';

  return (
    <div className={wrapperClass}>
      <div className="grid grid-cols-4 gap-2">
        <Button
          variant="outline"
          className="h-11 px-2 flex-col gap-0.5 border-primary/30 text-primary hover:bg-primary/5"
          onClick={handleEmail}
          aria-label="Send Email"
        >
          <Mail className="h-4 w-4" />
          <span className="text-[10px] font-bold uppercase tracking-wide">Email</span>
        </Button>
        <Button
          variant="outline"
          className="h-11 px-2 flex-col gap-0.5 border-primary/30 text-primary hover:bg-primary/5"
          asChild
          aria-label="Send SMS"
        >
          <a href={`sms:${cleanPhone}`}>
            <MessageSquare className="h-4 w-4" />
            <span className="text-[10px] font-bold uppercase tracking-wide">SMS</span>
          </a>
        </Button>
        <Button
          className={cn(
            'h-11 px-2 flex-col gap-0.5 font-bold text-primary-foreground',
            'bg-primary hover:bg-primary/90',
          )}
          asChild
          aria-label="Call"
        >
          <a href={`tel:${cleanPhone}`}>
            <Phone className="h-4 w-4" />
            <span className="text-[10px] uppercase tracking-wide">Call</span>
          </a>
        </Button>
        <Button
          className="h-11 px-2 flex-col gap-0.5 font-bold bg-whatsapp hover:bg-whatsapp/90 text-whatsapp-foreground"
          asChild
          aria-label="WhatsApp"
        >
          <a href={`https://wa.me/${waPhone}`} target="_blank" rel="noopener noreferrer">
            <MessageCircle className="h-4 w-4" />
            <span className="text-[10px] uppercase tracking-wide">WhatsApp</span>
          </a>
        </Button>
      </div>
    </div>
  );
}
